/* @jsx h */
import { defineComponent, ref, computed, h } from "vue";
import { Product, Promotion, Service } from "@lemmonhq/types";
import { cn, RightSheet, useLocalization } from "@lemmonhq/uikit";
import {
  getProductImageContainerStyle,
  getProductImageURL,
} from "@lemmonhq/blogic";
import { formatPrice } from "@lemmonhq/orders";
import {
  calculateProductPromotions,
  getPromotionBadgeText,
  ProductPromotionResult,
} from "@lemmonhq/orders";
import { PromotionInfoModal } from "../../components/PromotionInfoModal";
import { useMenu } from "@app/vue/providers/Menu";

export type ProductSelectionModalProps = {
  product: Product;
  onClose: Function;
  onConfirm: (
    product: Product,
    quantity: number,
    promotionResult: ProductPromotionResult,
    selectedBundles?: { bundleId: number; optionIds: number[] }[],
    notes?: string,
  ) => void;
  services: Service[];
  promotions: Promotion[];
};

export const MenuProductSelectModal =
  defineComponent<ProductSelectionModalProps>({
    name: "MenuProductSelectModal",
    props: ["product", "onClose", "onConfirm", "services", "promotions"],
    setup(props) {
      const { cart, shop } = useMenu();
      const { t } = useLocalization();
      const currency = computed(() => shop.value?.currency || "CHF");
      const quantity = ref(1);
      const selectedOptions = ref<Record<number, number[]>>({});
      const selectedBundles = ref<any[]>([]);
      const notes = ref("");
      const showNotes = ref(true);
      const showPromotionModal = ref(false);

      const isValid = computed(() =>
        (props.product.bundles || []).every((bundle) => {
          const selected = selectedOptions.value[bundle.id]?.length || 0;
          return selected >= bundle.min_limit && selected <= bundle.limit;
        }),
      );

      const handleOptionToggle = (bundleId: number, optionId: number) => {
        const currentSelected = selectedOptions.value[bundleId] || [];
        const isSelected = currentSelected.includes(optionId);
        const bundle = (props.product.bundles || []).find(
          (b) => b.id === bundleId,
        );

        if (!bundle) return;

        let newSelected: number[];
        if (isSelected) {
          newSelected = currentSelected.filter((id) => id !== optionId);
        } else {
          if (currentSelected.length >= bundle.limit) {
            newSelected = [...currentSelected.slice(1), optionId];
          } else {
            newSelected = [...currentSelected, optionId];
          }
        }

        selectedOptions.value = {
          ...selectedOptions.value,
          [bundleId]: newSelected,
        };
      };

      const promotionResult = computed(() => {
        if (!props.services || !props.promotions) {
          console.log("promotionResult returning", props);
          return {
            hasDiscountPromotions: false,
            totalDiscount: 0,
            finalPrice: parseFloat(props.product.price) * quantity.value,
            originalPrice: parseFloat(props.product.price) * quantity.value,
            nonDiscountPromotions: [],
            discountPromotions: [],
          };
        }

        const p = calculateProductPromotions(
          props.services,
          props.product.id,
          parseFloat(props.product.price),
          quantity.value,
          props.promotions,
          cart.cart.serviceMethod,
        );
        console.log("promotionResult", p);
        return p;
      });

      const totalPrice = computed(() => {
        let basePrice = parseFloat(props.product.price);
        const bundleOptionsPrice = selectedBundles.value.reduce(
          (total, bundle) => {
            const bundleConfig = props.product.bundles?.find(
              (b) => b.id === bundle.id,
            );
            if (bundleConfig) {
              const selectedOption = bundleConfig.options.find(
                (opt) => opt.id === bundle.selectedOptionId,
              );
              if (selectedOption) {
                return total + (selectedOption.price || 0);
              }
            }
            return total;
          },
          0,
        );

        const subtotalBeforePromotions =
          (basePrice + bundleOptionsPrice) * quantity.value;
        console.log("promotionResult.value", promotionResult.value);
        // Apply promotion discount only to the base product price, not bundle options
        if (promotionResult.value.hasDiscountPromotions) {
          const baseDiscount =
            promotionResult.value.originalPrice -
            promotionResult.value.finalPrice;
          return Math.max(0, subtotalBeforePromotions - baseDiscount);
        }

        return subtotalBeforePromotions;
      });

      return () => (
        <RightSheet
          title={props.product.name}
          isOpen={true}
          onClose={() => props.onClose()}
          class={"bg-base-200 h-full"}
          height="calc(100dvh - 45px)"
          style="top: 45px;"
        >
          <div class="p-4 ">
            {props.product.image_list && props.product.image_list.length > 0 ? (
              <div class="p-0 rounded-lg w-full">
                <div
                  class="product-img-holder overflow-hidden relative w-full rounded-xl"
                  style={getProductImageContainerStyle(props.product)}
                >
                  <div
                    class="absolute left-0 right-0 top-0 object-cover w-full h-full bg-cover bg-no-repeat bg-center"
                    style={`
                  background-image: url("${getProductImageURL(
                    props.product,
                    "single",
                  )}");
                  `}
                  />
                </div>
              </div>
            ) : null}

            <p class="py-4 text-xl">{props.product.description}</p>

            {/* Promotion Information */}
            {(promotionResult.value.hasDiscountPromotions ||
              promotionResult.value.nonDiscountPromotions.length > 0) && (
              <div class="mb-4 p-4 bg-success/10 rounded-lg">
                {promotionResult.value.hasDiscountPromotions && (
                  <div class="flex items-center gap-2 mb-2">
                    <span class=" font-medium text-success">
                      {t("customer.shop_menu.discounted_price")}
                    </span>
                    <span class="font-semibold text-success">
                      {formatPrice(
                        promotionResult.value.finalPrice,
                        true,
                        2,
                        currency.value,
                      )}
                    </span>
                    <span class="line-through text-sm opacity-60">
                      {formatPrice(
                        promotionResult.value.originalPrice,
                        true,
                        2,
                        currency.value,
                      )}
                    </span>
                  </div>
                )}

                {promotionResult.value.nonDiscountPromotions.length > 0 && (
                  <button
                    class="btn btn-sm btn-primary"
                    onClick={() => (showPromotionModal.value = true)}
                  >
                    🎁{" "}
                    {getPromotionBadgeText(
                      promotionResult.value.nonDiscountPromotions,
                    )}
                  </button>
                )}
              </div>
            )}
            <div class="flex py-6">
              <div class="flex flex-col justify-center items-center flex-1">
                <div class="flex items-center gap-4">
                  <button
                    onClick={() => quantity.value > 1 && quantity.value--}
                    class="btn btn-sm btn-circle btn-secondary"
                  >
                    <span class="material-symbols-outlined">remove</span>
                  </button>
                  <span class="text-2xl text-accent w-8 text-center">
                    {quantity.value}
                  </span>
                  <button
                    onClick={() => quantity.value++}
                    class="btn btn-sm btn-circle btn-secondary"
                  >
                    <span class="material-symbols-outlined">add</span>
                  </button>
                </div>
              </div>
              <div class="flex-1 hidden">
                <div class="flex flex-col justify-between items-center mb-4 ">
                  <span class="text-xl">{t("customer.checkout.total")}</span>
                  <span class="text-2xl">
                    {shop.value.currency} {totalPrice.value.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Bundle Options */}
            {props.product.bundles?.map((bundle) => (
              <div key={bundle.id} class="mb-6">
                <h3 class="text-xl mb-3 text-accent font-semibold space-y-1">
                  <div class="flex gap-1 justify-start items-center">
                    <div class="flex-1">{bundle.name}</div>
                    {bundle.min_limit ? (
                      <div class="badge badge-success capitalize">
                        {t("customer.shop_menu.required")}
                      </div>
                    ) : null}
                  </div>
                  <span class="text-sm">
                    {t("customer.shop_menu.select_count", [
                      bundle.min_limit,
                      bundle.limit,
                    ])}
                  </span>
                </h3>
                <div class="grid grid-cols-1 gap-2">
                  {bundle.options.map((option) => {
                    // todo: see why this doesn't work
                    const isSelected = !!selectedBundles.value.find(
                      (b) =>
                        b.id === bundle.id && b.selectedOptionId === option.id,
                    );
                    return (
                      <label
                        key={option.id}
                        class={cn(
                          "flex items-center justify-between p-4  rounded-lg cursor-pointer transition-colors",
                          isSelected ? "text-accent" : "",
                        )}
                      >
                        <div class="flex items-center gap-3">
                          <input
                            type="checkbox"
                            name={`bundle-${bundle.id}`}
                            class="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              handleOptionToggle(bundle.id, option.id)
                            }
                          />
                          <span>{option.display.title}</span>
                        </div>
                        {option.price > 0 ? (
                          <span>
                            +{option.price.toFixed(2)} {shop.value.currency}
                          </span>
                        ) : (
                          <span>
                            +{(0).toFixed(2)} {shop.value.currency}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            <div class="px-4">
              {showNotes.value ? (
                <>
                  <label class="form-control w-full ">
                    <div class="label hidden">
                      <span class="label-text font-semibold">
                        {t("customer.shop_menu.notes")}
                      </span>
                    </div>
                    <textarea
                      value={notes.value || ""}
                      onInput={(e) => {
                        notes.value = (e.target as HTMLTextAreaElement).value;
                      }}
                      class="textarea textarea-bordered p-2"
                      rows={2}
                      placeholder={t("customer.shop_menu.add_notes")}
                    />
                  </label>
                </>
              ) : (
                <>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      showNotes.value = true;
                    }}
                  >
                    {t("customer.shop_menu.add_notes_link")}
                  </a>
                </>
              )}
            </div>
          </div>

          <div class="sticky bottom-0  ">
            <div class="p-1 rounded  p-3 rounded flex">
              <div class="p-4 rounded rounded-lg flex-1 flex justify-center">
                <button
                  class={`btn btn-primary w-full mx-auto font-bold btn-lg ${
                    isValid.value ? "" : ""
                  }`}
                  disabled={!isValid.value}
                  onClick={() => {
                    const formattedSelections = (
                      props.product.bundles || []
                    ).map((bundle) => ({
                      bundleId: bundle.id,
                      optionIds: selectedOptions.value[bundle.id] || [],
                    }));
                    if (!isValid.value) return;
                    props.onConfirm(
                      props.product,
                      quantity.value,
                      promotionResult.value,
                      formattedSelections,
                      notes.value,
                    );
                  }}
                >
                  {t("customer.shop_menu.add_to_cart", [
                    totalPrice.value.toFixed(2),
                    shop.value.currency,
                  ])}
                </button>
              </div>
            </div>
          </div>

          {/* Promotion Info Modal */}
          {showPromotionModal.value && (
            <PromotionInfoModal
              promotions={promotionResult.value.nonDiscountPromotions}
              isOpen={showPromotionModal.value}
              onClose={() => (showPromotionModal.value = false)}
            />
          )}
        </RightSheet>
      );
    },
  });
